/**
 * A screen's filters, sort and the like (its URL query) remembered in this browser: opened bare (from the sidebar,
 * say), the screen comes back as it was left. A link with filters of its own (a count) wins, and becomes the new
 * memory. Search text and the page you were on aren't kept.
 */
import { useEffect, useRef } from "preact/hooks";
import { location, navigate } from "./router.ts";
import { store, stored } from "./storage.ts";

const PREFIX = "revisa.screen.";
/** Never remembered: what's typed in search, and where in the pages one was. */
const TRANSIENT = new Set(["q", "cursor"]);
/** What scopes a list to items linked to or from others (each repeatable). */
const SCOPED = ["refers_to", "linked_from"];

const read = (screen: string): string | null => stored(PREFIX + screen);

/** Keeps `query` for `screen` (none: forgets it). */
const write = (screen: string, query: string) => store(PREFIX + screen, query || null);

/**
 * Remembers the query of `screen` (identified by its `identity` params, like the entity of an items list, which
 * aren't part of what's remembered) and restores it when the screen is opened with nothing else.
 */
export function useRememberedQuery(screen: string, identity: readonly string[]): void {
  const opened = useRef<string | null>(null);
  const search = location.value.searchParams.toString();
  useEffect(() => {
    const params = new URLSearchParams(search);
    // Items linked to or from others (a group of the tree opened as a list, say): a view of its own, neither kept nor
    // replaced.
    if (SCOPED.some((k) => params.has(k))) {
      opened.current = screen;
      return;
    }
    const own = new URLSearchParams();
    for (const [k, v] of params) {
      if (!(identity.includes(k) || TRANSIENT.has(k))) {
        own.append(k, v);
      }
    }
    const first = opened.current !== screen;
    opened.current = screen;
    const saved = read(screen);
    if (first && own.size === 0 && saved && !params.has("q")) {
      const back = new URLSearchParams();
      for (const k of identity) {
        for (const v of params.getAll(k)) {
          back.append(k, v);
        }
      }
      for (const [k, v] of new URLSearchParams(saved)) {
        back.append(k, v);
      }
      navigate(`${location.value.pathname}?${back.toString()}`, true);
      return;
    }
    write(screen, own.toString());
  }, [screen, search, identity]);
}

/**
 * What makes an items (or issues) list a screen of its own: the entity or the kind picked in the sidebar. A folder's
 * page (and its tab) is that screen in a place: kept as it's opened, never remembered.
 */
const LIST_IDENTITY: readonly string[] = ["entity", "kind", "folder", "tab"];

/** Remembers the query of list `name` of a space, a screen per entity and kind picked; returns the screen's name. */
export function useListScreen(name: string, spaceId: string): string {
  const sp = location.value.searchParams;
  const screen = `${name}.${spaceId}.${sp.get("entity") ?? ""}.${sp.get("kind") ?? ""}`;
  useRememberedQuery(screen, LIST_IDENTITY);
  return screen;
}
